import { fromJS, isImmutable } from 'immutable';

import { mapExternalUrnToURL } from 'utils/mapUrnToUrl';
import featureFlagService, { featureKeys } from 'services/FeatureFlagService';

import { resolveConfig } from './graphQLResolvers';

import {
  URN_EXTERNAL,
} from './constants';
import { getSchemaVersion } from '../../utils/Schema';

const getJS = (data) => isImmutable(data) ? data.toJS() : data;

export const resolveAssetUrnsInState = async (assets, schema, state) => {
  // This function resolves all external and internal assets (in form of urns) into format expected by the plugins (urls).
  // This acts as a translation layer between format stored in Learnspace and format expected by plugins.

  const stateJS = getJS(state);

  if ((typeof stateJS !== 'object' && typeof stateJS !== 'string') || stateJS === null || !assets.length) {
    return stateJS;
  }

  const stateString = typeof stateJS === 'string' ? stateJS : JSON.stringify(stateJS);

  if (!stateString.includes('urn')) {
    return stateJS;
  }

  const isV2AssetSchema = getSchemaVersion(schema).major >= 2;
  // To avoid the unnecessary calls filtering the external assets those need to be resolve
  const externalAssets = [];
  const internalAssets = [];

  const assetsJS = isImmutable(assets) ? assets.toJS() : assets;
  assetsJS.forEach((asset) => {
    if (stateString.includes(asset.urn)) {
      if (asset.urn.startsWith(URN_EXTERNAL)) {
        externalAssets.push(asset);
      } else {
        internalAssets.push(asset);
      }
    }
  });

  /*
    There are special cases where state get or subscribe is not targetting a walkable but a particular field within a walkable. For example
    coursewareId > state > background > image
    If this state value is a string, we need to resolve any urns present

    TODO - This entire solution (how and when we resolve assets) is scheduled be replaced by an updated solution thats already been implemented inside BV.
    Learnspace ticket: https://agile-jira.pearson.com/browse/BRNT-27961
    BV ticket (implemented):
    https://agile-jira.pearson.com/browse/BRNT-22717
    https://agile-jira.pearson.com/browse/BRNT-27686
  */

  if (typeof stateJS === 'string') {
    let statePart = stateJS;
    if (externalAssets.length) {
      const newStateData = await mapExternalUrnToURL(externalAssets, statePart, isV2AssetSchema);
      statePart = newStateData;
    }
    return statePart;
  }

  let stateImmutable = isImmutable(state) ? state : fromJS(state);

  let newStateData = null;
  if (externalAssets.length || internalAssets.length) {
    if (externalAssets.length) {
      newStateData = await mapExternalUrnToURL(externalAssets, stateString, isV2AssetSchema);
      stateImmutable = stateImmutable.setIn([], fromJS({ ...stateImmutable.toJS(), ...newStateData }));
    }

    const allowInternalAssetResolve = featureFlagService.is(featureKeys.AUTHORING_PREVENT_CONFIG_ASSET_RESOLVE);
    if (internalAssets.length && allowInternalAssetResolve) {
      newStateData = await resolveConfig({ config: stateString }, assets, schema);
      stateImmutable = stateImmutable.setIn([], fromJS({ ...stateImmutable.toJS(), ...newStateData }));
    }
  }

  return stateImmutable.toJS();
};
